"use client";

import type * as React from "react";

import { cx } from "../../lib/cx";
import { Icon, type IconName } from "../core/Icon";
import s from "./SegmentedControl.module.css";

export interface SegmentedOption<V extends string = string> {
  value: V;
  label: React.ReactNode;
  icon?: IconName;
  disabled?: boolean;
  /** Sólo el icono a la vista; el `label` (string) queda como nombre accesible y tooltip. */
  hideLabel?: boolean;
}

export interface SegmentedControlProps<V extends string = string>
  extends Omit<React.ComponentPropsWithoutRef<"div">, "onChange"> {
  /** Qué elige el control, para el lector de pantalla: "Vista". */
  label: string;
  options: SegmentedOption<V>[];
  value: V;
  onChange?: (value: V) => void;
  size?: "sm" | "md";
  /** sunken = píldora gris con la elegida blanca (default); outline = píldora blanca con borde y la elegida en accent-50. */
  tone?: "sunken" | "outline";
  /** Ocupa todo el ancho y reparte las opciones en partes iguales (el tipo de pedido dentro de un diálogo). */
  fullWidth?: boolean;
}

/** Lista / Mapa / Galería: una sola opción entre pocas, siempre visible. Radio group con flechas. */
export function SegmentedControl<V extends string = string>({
  label,
  options,
  value,
  onChange,
  size = "md",
  tone = "sunken",
  fullWidth = false,
  className,
  ...rest
}: SegmentedControlProps<V>) {
  function mover(e: React.KeyboardEvent<HTMLDivElement>) {
    const teclas: Record<string, 1 | -1> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const paso = teclas[e.key];
    if (!paso) return;
    const habilitadas = options.filter((o) => !o.disabled);
    if (habilitadas.length === 0) return;
    e.preventDefault();
    const actual = habilitadas.findIndex((o) => o.value === value);
    const siguiente = habilitadas[(actual + paso + habilitadas.length) % habilitadas.length];
    onChange?.(siguiente.value);
    (e.currentTarget.querySelector(`[data-value="${siguiente.value}"]`) as HTMLButtonElement | null)?.focus();
  }
  return (
    <div role="radiogroup" aria-label={label} className={cx(s.grupo, className)} data-size={size} data-tone={tone} data-full-width={fullWidth || undefined} onKeyDown={mover} {...rest}>
      {options.map((o) => {
        const elegida = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={elegida}
            className={s.opcion}
            data-value={o.value}
            disabled={o.disabled}
            tabIndex={elegida ? 0 : -1}
            data-icon-only={o.hideLabel ? "" : undefined}
            aria-label={o.hideLabel && typeof o.label === "string" ? o.label : undefined}
            title={o.hideLabel && typeof o.label === "string" ? o.label : undefined}
            onClick={() => onChange?.(o.value)}
          >
            {o.icon ? <Icon name={o.icon} size={size === "sm" ? 13 : 15} /> : null}
            {o.hideLabel ? null : o.label}
          </button>
        );
      })}
    </div>
  );
}
